import { withPageContainerStyles } from '@/common/components/page-layout';
import Spacer from '@/common/components/spacer';
import PDPSectionContainer, { PDPSectionContainerProps } from './pdp-section-container';
import { cn } from '@/shadcn-ui/utils';
import QuestionsAnswers from './questions-answers';

interface WhyFlexcarProps
  extends Pick<PDPSectionContainerProps, 'onInViewChange' | 'addSectionRef'> {
  cdnUrl: string;
}

const copy = [
  {
    title: 'Swap or return anytime',
    subTitle:
      'Drive what you want, when you want. Jeep in the summer. Explorer in the winter. Your car should adapt to your life, not the other way around.',
    imageUrl: 'web/images/pdp/summer-jeep.webp',
    imageAlt: 'Jeep Wrangler by the coastline at sunset',
    tileGradient: true,
  },
  {
    title: 'Superior savings',
    subTitle:
      'Never overpay for your car again. Forget the down payment. Skip the hidden fees. Pay less at the pump. Save 20% more every month.',
    imageUrl: 'web/images/pdp/sunoco-station.webp',
    imageAlt: 'Sunoco station on a starry night',
  },
  {
    title: 'Insurance & maintenance included',
    subTitle:
      "No surprise repair bills. Oil change, new tires, brake work, engine issues — it's all covered so you can just drive.",
    imageUrl: 'web/images/pdp/car-cornering-in-forest.webp',
    imageAlt: 'Car cornering surrounded by a forest',
    imagePosition: 'bottom',
  },
];

export default function WhyFlexcar({
  onInViewChange,
  addSectionRef,
  cdnUrl,
}: WhyFlexcarProps) {
  const copyTiles = copy.map((item) => (
    <div
      className="relative flex h-[524px] w-full snap-center flex-col gap-[8px] overflow-hidden rounded-[32px] px-[32px] py-[40px] md:h-[400px] md:px-[24px] md:py-[40px] lg:h-[516px] lg:w-[384px] lg:flex-shrink-0"
      key={item.title}
    >
      <img
        src={`${cdnUrl}/${item.imageUrl}`}
        alt={item.imageAlt}
        className={cn('absolute inset-0 z-0 h-full w-full object-cover', {
          'object-bottom': item.imagePosition === 'bottom',
        })}
        fetchPriority="auto"
        loading="lazy"
      />
      {item.tileGradient ? (
        <div className="absolute inset-x-0 top-0 z-[1] h-[50%] bg-[linear-gradient(180deg,rgba(0,0,0,0.7)_0%,rgba(0,0,0,0.00)_100%)]" />
      ) : null}
      <div className="relative z-[2]">
        <h4 className="header-sans-tight text-flexcar-neutral100">{item.title}</h4>
        <Spacer variant="height28" />
        <p className="body-tight text-[18px] text-flexcar-neutral100">{item.subTitle}</p>
      </div>
    </div>
  ));

  return (
    <PDPSectionContainer
      sectionKey="whyFlexcar"
      onInViewChange={onInViewChange}
      addSectionRef={addSectionRef}
      threshold={0.2}
      className="flex flex-col"
    >
      <div className="flex w-full flex-row justify-center bg-flexcar-neutral100">
        <div
          className={withPageContainerStyles(
            'max-w-[1440px] px-0 pt-[88px] md:px-[24px] md:pt-[120px] lg:px-[120px]'
          )}
        >
          <div className="hidden flex-col gap-[8px] md:flex">
            <h1 className="header-sans-tight text-center">Why Flexcar?</h1>

            <h2 className="header-sans-tight text-center">
              Benefits on benefits on benefits.
            </h2>
          </div>

          <div className="flex flex-col gap-[8px] md:hidden">
            <h3 className="header-sans-tight text-center">Why Flexcar?</h3>

            <p className="body-tight text-center text-[18px]">
              Benefits on benefits on benefits.
            </p>
          </div>

          <Spacer variant="height80" hideForBreakpoints={['sm']} />
          <Spacer variant="height40" hideForBreakpoints={['md', 'lg', 'xl', '2xl']} />
          <div className="flex flex-col gap-[24px] lg:hidden">{copyTiles}</div>
        </div>
      </div>
      <div className="scrollbar-hide mx-[24px] hidden w-full snap-x snap-mandatory flex-row gap-[24px] overflow-x-auto scroll-smooth bg-flexcar-neutral100 will-change-transform lg:flex lg:justify-start xl:justify-center">
        {/*  Empty div to for centering at start of scroll box */}
        <div></div>
        {copyTiles}
        {/*  Empty div to for an extra gap at end of scroll box */}
        <div></div>
      </div>
      <QuestionsAnswers />
    </PDPSectionContainer>
  );
}
